import React from 'react';
import PropTypes from 'prop-types';

/**
 * The shared furniture for the read-only panels that render what a "Sync from
 * Opla" button pulled down. Four of these used to be four hand-built tables
 * with four slightly different header stylings; the shape is the same, so it
 * lives here once.
 */

export const OplaEmpty = ({ children }) => (
  <p className="tw-text-xs tw-text-slate-400 tw-mb-0 tw-italic">{children}</p>
);
OplaEmpty.propTypes = { children: PropTypes.node };

export const OplaPanel = ({ footer, children }) => (
  <div className="tw-flex tw-flex-col tw-gap-1.5">
    <div className="tw-rounded-lg tw-border tw-border-slate-200 tw-overflow-hidden tw-bg-white">
      {children}
    </div>
    {footer && <p className="tw-text-[11px] tw-text-slate-400 tw-text-right tw-mb-0">{footer}</p>}
  </div>
);
OplaPanel.propTypes = { footer: PropTypes.node, children: PropTypes.node };

export const Check = ({ on }) =>
  on ? <span className="tw-text-emerald-600 tw-font-semibold">✓</span>
     : <span className="tw-text-slate-300">—</span>;
Check.propTypes = { on: PropTypes.bool };

export const Swatch = ({ color }) => {
  if (!color) return <span className="tw-text-slate-300">—</span>;
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
      <span className="tw-w-3 tw-h-3 tw-rounded tw-border tw-border-slate-200 tw-shrink-0" style={{ backgroundColor: color }} />
      <span className="tw-text-[11px] tw-font-mono tw-text-slate-500">{color}</span>
    </span>
  );
};
Swatch.propTypes = { color: PropTypes.string };

export const OplaId = ({ id }) => (
  <span className="tw-font-mono tw-text-[11px] tw-text-slate-400 tw-truncate tw-block">{id || '—'}</span>
);
OplaId.propTypes = { id: PropTypes.string };

/**
 * A table driven by a column spec: { header, width, align, cell }.
 * `cell` receives (row, index) and returns a node.
 */
export const OplaTable = ({ columns, rows, rowKey }) => (
  <table className="tw-w-full tw-table-fixed tw-text-xs">
    <thead className="tw-bg-slate-50 tw-border-b tw-border-slate-200">
      <tr>
        {columns.map((col) => (
          <th
            key={col.header}
            style={col.width ? { width: col.width } : undefined}
            className={`tw-px-3 tw-py-2 tw-font-semibold tw-text-slate-500 tw-text-[11px] tw-uppercase tw-tracking-wide ${
              col.align === 'center' ? 'tw-text-center' : 'tw-text-left'
            }`}
          >
            {col.header}
          </th>
        ))}
      </tr>
    </thead>
    <tbody className="tw-divide-y tw-divide-slate-100">
      {rows.map((row, i) => (
        <tr key={rowKey?.(row, i) ?? i} className="hover:tw-bg-slate-50/70 tw-transition-colors">
          {columns.map((col) => (
            <td
              key={col.header}
              className={`tw-px-3 tw-py-2 tw-text-slate-700 ${col.align === 'center' ? 'tw-text-center' : ''} ${col.truncate ? 'tw-truncate' : ''}`}
            >
              {col.cell(row, i)}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);
OplaTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.shape({
    header: PropTypes.string.isRequired,
    width: PropTypes.string,
    align: PropTypes.string,
    truncate: PropTypes.bool,
    cell: PropTypes.func.isRequired,
  })).isRequired,
  rows: PropTypes.array.isRequired,
  rowKey: PropTypes.func,
};

// Every viewer reads a JSON string out of one setting value.
export const parseJson = (value, fallback) => {
  try { return JSON.parse(value || JSON.stringify(fallback)); } catch { return fallback; }
};
